import {
  normalizeAnalyticsId,
  runIsolatedAnalyticsTask,
  shouldEnableAnalytics
} from './analyticsGate.js';

export const FUNNEL_EVENTS = Object.freeze({
  AUTH_OPEN: 'funnel_auth_open',
  AUTH_SUCCESS: 'funnel_auth_success',
  PAY_PAGE_OPEN: 'funnel_pay_page_open',
  PAY_QR_SHOW: 'funnel_pay_qr_show',
  PAY_SUCCESS: 'funnel_pay_success',
  UPLOAD_START: 'funnel_upload_start',
  UPLOAD_SUCCESS: 'funnel_upload_success',
  QUOTA_BLOCK: 'funnel_quota_block',
  TRANSLATION_START: 'funnel_translation_start',
  TRANSLATION_SUCCESS: 'funnel_translation_success',
  TRANSLATION_FAIL: 'funnel_translation_fail'
});

const FUNNEL_VERSION = 'v1';
const UNKNOWN_FILE_TYPE = 'unknown';
const dedupeStore = new Set();

const FILE_RELATED_EVENTS = new Set([
  FUNNEL_EVENTS.UPLOAD_START,
  FUNNEL_EVENTS.UPLOAD_SUCCESS,
  FUNNEL_EVENTS.QUOTA_BLOCK,
  FUNNEL_EVENTS.TRANSLATION_START,
  FUNNEL_EVENTS.TRANSLATION_SUCCESS,
  FUNNEL_EVENTS.TRANSLATION_FAIL
]);

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function readMeasurementIdFromRuntime() {
  try {
    if (typeof useRuntimeConfig !== 'function') {
      return '';
    }
    const runtime = useRuntimeConfig();
    return runtime?.public?.googleAnalyticsMeasurementId ?? '';
  } catch {
    return '';
  }
}

function resolveNodeEnv(nodeEnv) {
  if (typeof nodeEnv === 'string') {
    return nodeEnv;
  }
  if (typeof process !== 'undefined' && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV;
  }
  return '';
}

function resolveMeasurementId(measurementId) {
  if (typeof measurementId === 'string') {
    return normalizeAnalyticsId(measurementId);
  }
  return normalizeAnalyticsId(readMeasurementIdFromRuntime());
}

function resolveClientFlag(runtime) {
  if (typeof runtime.isClient === 'boolean') {
    return runtime.isClient;
  }
  return import.meta.client === true;
}

function resolveGtag(runtime) {
  if (typeof runtime.gtag === 'function') {
    return runtime.gtag;
  }
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.gtag;
}

function normalizeDedupeKey(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function sanitizeEventParams(params) {
  if (!isPlainObject(params)) {
    return {};
  }

  const payload = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }
    payload[key] = value;
  }
  return payload;
}

function normalizeEntityId(entityId) {
  if (typeof entityId === 'number' && Number.isFinite(entityId)) {
    return String(entityId);
  }
  if (typeof entityId === 'string') {
    const normalized = entityId.trim();
    return normalized.length > 0 ? normalized : '';
  }
  return '';
}

export function resetFunnelEventDedupe() {
  dedupeStore.clear();
}

export function extractFileType(fileName) {
  if (typeof fileName !== 'string') {
    return UNKNOWN_FILE_TYPE;
  }

  const normalized = fileName.trim();
  const dotIndex = normalized.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex >= normalized.length - 1) {
    return UNKNOWN_FILE_TYPE;
  }

  const ext = normalized.slice(dotIndex + 1).toLowerCase();
  if (!/^[a-z0-9]+$/.test(ext)) {
    return UNKNOWN_FILE_TYPE;
  }
  return ext;
}

export function buildFileEventParams(fileName, extraParams = {}) {
  const payload = sanitizeEventParams(extraParams);
  payload.file_type = extractFileType(fileName);
  return payload;
}

export function buildPayQrDedupeKey(orderId) {
  const normalized = normalizeEntityId(orderId);
  return normalized ? `pay_qr:${normalized}` : '';
}

export function buildPaySuccessDedupeKey(orderId) {
  const normalized = normalizeEntityId(orderId);
  return normalized ? `pay_success:${normalized}` : '';
}

export function buildTranslationTerminalDedupeKey(recordId) {
  const normalized = normalizeEntityId(recordId);
  return normalized ? `translation_terminal:${normalized}` : '';
}

export function trackFunnelEvent(eventName, eventParams = {}, options = {}) {
  const normalizedEventName = typeof eventName === 'string' ? eventName.trim() : '';
  if (!normalizedEventName) {
    return false;
  }

  const runtime = isPlainObject(options.runtime) ? options.runtime : {};
  if (!resolveClientFlag(runtime)) {
    return false;
  }

  const nodeEnv = resolveNodeEnv(runtime.nodeEnv);
  const measurementId = resolveMeasurementId(runtime.measurementId);
  if (!shouldEnableAnalytics({ id: measurementId, nodeEnv })) {
    return false;
  }

  const gtag = resolveGtag(runtime);
  if (typeof gtag !== 'function') {
    return false;
  }

  const dedupeKey = normalizeDedupeKey(options.dedupeKey);
  if (dedupeKey && dedupeStore.has(dedupeKey)) {
    return false;
  }

  const payload = sanitizeEventParams(eventParams);
  if (FILE_RELATED_EVENTS.has(normalizedEventName) && !('file_type' in payload)) {
    payload.file_type = UNKNOWN_FILE_TYPE;
  }
  payload.funnel_version = FUNNEL_VERSION;

  if (dedupeKey) {
    dedupeStore.add(dedupeKey);
  }

  const ok = runIsolatedAnalyticsTask(() => {
    gtag('event', normalizedEventName, payload);
  });

  if (!ok && dedupeKey) {
    dedupeStore.delete(dedupeKey);
  }

  return ok;
}
